import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  awaitingReply,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES,
  type DocumentType,
  describeError,
  present,
  today,
} from '@horof/shared';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Select,
  Table,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from 'urql';
import {
  type DocumentRow,
  DocumentsDocument,
  documentsContext,
  useCanFile,
} from './association-data';
import { DocumentPanel } from './DocumentPanel';
import { useLists } from './data';
import { DOCUMENT_FILTER_KEYS, documentsFilter } from './documents-filter';
import { useSplit } from './panel';
import { useFitColumns } from './table-fit';
import { ListHeader } from './ui';

/**
 * «الوثائق»: the association's papers (arqam doc 52). Filters in the address
 * (type, search on title and reference, year, project, counterparty, letters
 * awaiting a reply); a row opens its panel with the files.
 */
export function DocumentsScreen() {
  const canFile = useCanFile();
  const split = useSplit();
  const { ref: fitRef, fit } = useFitColumns();
  const [params, setParams] = useSearchParams();
  const now = today();
  const filter = useMemo(() => documentsFilter(params, now), [params, now]);
  const [{ data, error, fetching }] = useQuery({
    query: DocumentsDocument,
    variables: { filter },
    context: documentsContext,
  });
  const [{ data: lists }] = useLists();
  const rows = present(data?.associationDocuments?.nodes);
  const type = params.get('type') as DocumentType | null;

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <ListHeader
          title="الوثائق"
          count={data?.associationDocuments?.totalCount}
          description="أوراق الجمعية: القانون الأساسي، المحاضر، المراسلات، العقود…"
          actions={
            canFile && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => split.open('new')}
              >
                وثيقة جديدة
              </Button>
            )
          }
        />
        <div className="ui-table-card">
          <div className="desk-toolbar">
            <Select<DocumentType>
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="كل الأنواع"
              className="desk-filter is-wide"
              value={type ?? undefined}
              onChange={(v) => update({ type: v ?? null })}
              options={DOCUMENT_TYPES.map((t) => ({
                value: t,
                label: DOCUMENT_TYPE_LABELS[t],
              }))}
            />
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="بحث في العنوان أو المرجع"
              className="desk-search"
              value={params.get('q') ?? ''}
              onChange={(e) => update({ q: e.target.value })}
            />
            <DatePicker
              picker="year"
              placeholder="السنة"
              value={
                params.get('year') ? dayjs(`${params.get('year')}-01-01`) : null
              }
              onChange={(d) => update({ year: d ? d.format('YYYY') : null })}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="الجهة"
              className="desk-filter"
              value={params.get('counterparty') ?? undefined}
              onChange={(v) => update({ counterparty: v ?? null })}
              options={present(lists?.counterparties?.nodes).map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="المشروع"
              className="desk-filter"
              value={params.get('project') ?? undefined}
              onChange={(v) => update({ project: v ?? null })}
              options={present(lists?.projects?.nodes).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
            <Checkbox
              checked={!!params.get('waiting')}
              onChange={(e) =>
                update({ waiting: e.target.checked ? '1' : null })
              }
            >
              تنتظر الرد
            </Checkbox>
            {DOCUMENT_FILTER_KEYS.some((k) => params.get(k)) && (
              <Button
                type="link"
                onClick={() =>
                  update(
                    Object.fromEntries(
                      DOCUMENT_FILTER_KEYS.map((k) => [k, null]),
                    ),
                  )
                }
              >
                إلغاء التصفية
              </Button>
            )}
          </div>

          {error && (
            <Alert
              type="error"
              showIcon
              message={describeError(error, 'تعذّر تحميل الوثائق.')}
            />
          )}

          <Table<DocumentRow>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            dataSource={rows}
            loading={fetching && !data}
            pagination={{ pageSize: 50, size: 'small', hideOnSinglePage: true }}
            scroll={{ y: 'calc(100vh - 260px)' }}
            rowClassName={(d) =>
              d.id === split.panel ? 'is-open is-clickable' : 'is-clickable'
            }
            onRow={(d) => ({ onClick: () => split.open(d.id) })}
            locale={{ emptyText: 'لا وثائق بهذا الاختيار' }}
            columns={fit<DocumentRow>([
              {
                key: 'date',
                title: 'التاريخ',
                width: 100,
                render: (_, d) => (
                  <span className="ltr-num">{d.documentDate}</span>
                ),
              },
              {
                key: 'reference',
                drop: 3,
                title: 'المرجع',
                width: 100,
                render: (_, d) => (
                  <span className="ltr-num">{d.reference}</span>
                ),
              },
              {
                key: 'title',
                title: 'العنوان',
                render: (_, d) => (
                  <span>
                    {d.title}{' '}
                    {d.isCurrent && <Tag color="green">النسخة المعتمدة</Tag>}
                    {awaitingReply(d, now) && (
                      <Tag color="orange">ينتظر الرد</Tag>
                    )}
                    {!type && (
                      <div className="field-hint">
                        {DOCUMENT_TYPE_LABELS[d.type]}
                      </div>
                    )}
                  </span>
                ),
              },
              {
                key: 'counterparty',
                drop: 4,
                title: 'الجهة',
                width: 170,
                ellipsis: true,
                render: (_, d) => d.counterparty?.name,
              },
              {
                key: 'project',
                drop: 2,
                title: 'المشروع',
                width: 170,
                ellipsis: true,
                render: (_, d) => d.project?.name,
              },
              {
                key: 'files',
                drop: 1,
                title: 'الملفات',
                width: 70,
                render: (_, d) =>
                  d.files.totalCount ? (
                    <span className="ltr-num">{d.files.totalCount}</span>
                  ) : (
                    <span className="field-hint">—</span>
                  ),
              },
            ])}
          />
        </div>
      </section>

      {split.panel && (
        <DocumentPanel
          key={split.panel}
          id={split.panel === 'new' ? null : split.panel}
          presetType={type}
          onClose={split.close}
          onOpen={split.open}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </div>
  );
}
